/**
 * The stacking order, in one place.
 *
 * Every z-index in the design system and the apps comes from here. Before this
 * existed each author picked a number and the numbers argued: page cards at
 * `z-[100]` painted over dialogs at 50, a sort pill sat at `z-[1100]`, and one
 * overlay reached for `z-[1000000]`. Raising whichever layer lost the argument
 * just moved the fight along; naming the bands ends it.
 *
 * The one rule the bands encode: **page chrome sits below a dialog, and only
 * the popover tier sits above one.** A menu, dropdown, tooltip or date picker
 * has to be able to open from inside a dialog, so it is the single tier allowed
 * to outrank it — everything else on the page must lose to a dialog, or the
 * dialog is not a dialog.
 *
 * Two shapes because there are two consumers: `layerClass` for Tailwind (the
 * JIT needs a literal class name, so these are written out), and `LAYER` for
 * the places that compute — `Dialog` adds `dialogStride` per stack position, so
 * a confirm opened from a dialog lands above its parent.
 *
 * Adopted so far by the dialog stack and by app page chrome. The popover tier
 * is defined but not yet adopted — see the note on it.
 */

export const LAYER = {
  /** Page chrome that has to cover a sibling: a sticky bar, a card that
   *  overlaps the header above it. */
  raised: 10,
  /** App furniture pinned over scrolling content — the nav bar, a page header. */
  sticky: 20,
  /** A full-page backdrop the app itself owns, e.g. a loading gate. */
  backdrop: 30,
  dialogOverlay: 40,
  dialogContent: 50,
  /** Added per open dialog, so a stack keeps its order. */
  dialogStride: 20,
  /**
   * Offsets *within* one depth band, all smaller than `dialogStride` so the
   * next dialog still clears them. Order inside a band: the surface, then any
   * popover it opens, then a tooltip on that popover, then a confirm.
   */
  popoverOffset: 5,
  tooltipOffset: 7,
  confirmOffset: 10,
  /**
   * Base for a popover with no dialog above it. Above every page band, below
   * `dialogOverlay`, so a dialog opened later still covers an open menu.
   */
  pagePopoverBase: 30,
  /**
   * Collaborator presence — the cursors and field masks drawn over another
   * user's edits. Knowingly above dialogs *and* popovers: it has to show who is
   * editing whatever you just opened. The only band that outranks a dialog
   * without being a popover, and the reason it is spelled out here rather than
   * being another loose number in a feature.
   */
  presence: 200,
} as const;

/**
 * Where an overlay sits, given the depth of the dialog it was declared inside
 * (-1 for none) and its offset within that band.
 *
 * Stacking has one rule and it is not "dialogs above popovers" — it is that
 * **whatever opened last is on top**. A menu opened from a dialog must cover
 * it; a confirm raised from that menu must cover the menu; a second dialog
 * must cover all of it. A fixed number for popovers can only ever satisfy one
 * of those, which is why a flat 9999 hid the confirmation a menu item asked
 * for. Deriving from the dialog depth satisfies all three, because a confirm
 * or a nested dialog registers one level deeper and the stride carries it
 * clear.
 */
export function overlayZIndex(dialogDepth: number, offset: number): number {
  return dialogDepth < 0
    ? LAYER.pagePopoverBase + offset
    : LAYER.dialogContent + dialogDepth * LAYER.dialogStride + offset;
}

export const layerClass = {
  raised: "z-10",
  sticky: "z-20",
  backdrop: "z-30",
  dialogOverlay: "z-40",
  dialogContent: "z-50",
  /** A page-level popover that isn't portaled through ui-core — hubble's nav
   *  menus. Same value `usePopoverZIndex` resolves to outside a dialog. */
  pagePopover: "z-[35]",
  presence: "z-[200]",
} as const;
